import React, { useRef } from 'react';

import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faXmark } from '@fortawesome/pro-regular-svg-icons';

import { formatBytes } from 'helpers/formatters/file_size_formats';

import { Button } from 'components/ui/button';
import TrixEditor from 'components/ui/trix/trix_editor';
import { getFileName, getFileSize } from './helpers';

import type { EmailFile, EmailFormState } from './types';

interface Props {
  form: EmailFormState;
  files: EmailFile[];
  showCc: boolean;
  showBcc: boolean;
  onChange: (changes: Partial<EmailFormState>) => void;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onToggleCc: () => void;
  onToggleBcc: () => void;
}

export default function SendEmailForm({
  form,
  files,
  showCc,
  showBcc,
  onChange,
  onAddFiles,
  onRemoveFile,
  onToggleCc,
  onToggleBcc,
}: Props) {
  // No Blueprint file-picker primitive exists, so a hidden native input driven by a Button is the
  // closest fit.
  const fileInput = useRef<HTMLInputElement>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onAddFiles(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  return (
    <div className="d-flex flex-column gap-3">
      <Field className="form-group">
        <div className="d-flex justify-content-between align-items-center">
          <Field.Label htmlFor="sendEmailTo">To</Field.Label>
          <div className="d-flex gap-2">
            {!showCc && <Button label="Cc" size="sm" variant="ghost" onClick={onToggleCc} />}
            {!showBcc && <Button label="Bcc" size="sm" variant="ghost" onClick={onToggleBcc} />}
          </div>
        </div>
        <Input
          id="sendEmailTo"
          placeholder="recipient@example.com"
          value={form.to}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ to: e.target.value })}
        />
      </Field>

      {showCc && (
        <Field className="form-group">
          <Field.Label htmlFor="sendEmailCc">Cc</Field.Label>
          <Input
            id="sendEmailCc"
            value={form.cc}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ cc: e.target.value })}
          />
        </Field>
      )}

      {showBcc && (
        <Field className="form-group">
          <Field.Label htmlFor="sendEmailBcc">Bcc</Field.Label>
          <Input
            id="sendEmailBcc"
            value={form.bcc}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ bcc: e.target.value })}
          />
        </Field>
      )}

      <Field className="form-group">
        <Field.Label htmlFor="sendEmailSubject">Subject</Field.Label>
        <Input
          id="sendEmailSubject"
          value={form.subject}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ subject: e.target.value })}
        />
      </Field>

      <Field className="form-group">
        <Field.Label htmlFor="sendEmailBody">Body</Field.Label>
        <TrixEditor
          id="sendEmailBody"
          initialValue={form.body}
          name="sendEmailBody"
          onChange={body => onChange({ body })}
        />
      </Field>

      <Field className="form-group">
        <div className="d-flex justify-content-between align-items-center">
          <Field.Label>Attachments</Field.Label>
          <Button
            icon={faPaperclip}
            label="Attach files"
            size="sm"
            variant="ghost"
            onClick={() => fileInput.current?.click()}
          />
        </div>
        <input className="d-none" multiple ref={fileInput} type="file" onChange={handleFileInput} />
        {files.length > 0 && (
          <div className="d-flex flex-column gap-2 mt-2">
            {files.map((file, index) => (
              <div
                className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                key={`${getFileName(file)}-${index}`}
              >
                <span className="text-truncate me-2">
                  <FontAwesomeIcon className="me-2 text-muted" icon={faPaperclip} />
                  {getFileName(file)}
                  <span className="text-muted fs-xs ms-2">{formatBytes(getFileSize(file))}</span>
                </span>
                <Button
                  aria-label={`Remove ${getFileName(file)}`}
                  icon={faXmark}
                  size="sm"
                  variant="ghost"
                  onClick={() => onRemoveFile(index)}
                />
              </div>
            ))}
          </div>
        )}
      </Field>
    </div>
  );
}
